import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import departmentService from '../../services/departmentService';
import DepartmentCard from './DepartmentCard';
import DepartmentTree from './DepartmentTree';
import './DepartmentList.css';
import { FaBuilding, FaChartLine, FaCheckCircle, faCircleCheck, FaRegCheckCircle, FaRupeeSign } from 'react-icons/fa';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import CommonModal from '../../services/CommonModal';

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'tree'
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    parent_department_id: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalBudget: 0,
    totalSpent: 0
  });

  useEffect(() => {
    fetchDepartments();
  }, [filters, viewMode]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);

      if (viewMode === 'tree') {
        const data = await departmentService.getDepartmentHierarchy();
        setHierarchy(data.hierarchy || []);
        calculateStats(departmentService.flattenDepartmentTree(data.hierarchy));
      } else {
        const data = await departmentService.getAllDepartments(filters);
        setDepartments(data.departments || []);
        calculateStats(data.departments);
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch departments');
      setDepartments([]);
      setHierarchy([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const calculateStats = (depts) => {
    const active = depts.filter(d => d.is_active).length;
    const totalBudget = depts.reduce((sum, d) => sum + parseFloat(d.budget_allocated || 0), 0);
    const totalSpent = depts.reduce((sum, d) => sum + parseFloat(d.budget_spent || 0), 0);

    setStats({
      total: depts.length,
      active,
      totalBudget,
      totalSpent
    });
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);


  const confirmDelete = () => {
    if (selectedId) {
      handleDelete(selectedId); // your existing delete API/function
    }

    setShowDeleteModal(false);
    setSelectedId(null);
  };

  const handleDelete = async (id) => {
    try {
      await departmentService.deleteDepartment(id);
      fetchDepartments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete department');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const budgetUtilization = stats.totalBudget > 0
    ? ((stats.totalSpent / stats.totalBudget) * 100).toFixed(2)
    : 0;

  return (
    <div className="department-list-container">
      <div className="department-list-header">
        <h1>Departments</h1>
        <Link to="/app/departments/new" className="btn btn-primary">
          <i className="fas fa-plus"></i> Create Department
        </Link>
      </div>

      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Department"
        message="Are you sure you want to delete this department?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedId(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Statistics Cards */}
<div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-2 xl:grid-cols-4">
  {/* Total Departments */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-blue-100">
      <FaBuilding className="text-2xl text-blue-600" />
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">
        Total Departments
      </p>
      <h3 className="text-2xl font-bold text-gray-800">
        {stats.total}
      </h3>
    </div>
  </div>

  {/* Active Departments */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl badge-success">
      <FaRegCheckCircle className="text-2xl badge-success" />
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">
        Active Departments
      </p>
      <h3 className="text-2xl font-bold text-gray-800">
        {stats.active}
      </h3>
    </div>
  </div>

  {/* Total Budget */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl badge-warning">
      <FaIndianRupeeSign className="text-2xl badge-warning" />
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">
        Total Budget
      </p>
      <h3 className="text-2xl font-bold text-gray-800">
        {departmentService.formatBudget(stats.totalBudget)}
      </h3>
    </div>
  </div>

  {/* Budget Utilization */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-pink-100">
      <FaChartLine className="text-2xl text-pink-600" />
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">
        Budget Utilization
      </p>
      <h3 className="text-2xl font-bold text-gray-800">
        {budgetUtilization}%
      </h3>
    </div>
  </div>
</div>

      {/* View Mode Toggle and Filters */}
      <div className="controls-section">
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <i className="fas fa-th"></i> Grid View
          </button>
          <button
            className={`toggle-btn ${viewMode === 'tree' ? 'active' : ''}`}
            onClick={() => setViewMode('tree')}
          >
            <i className="fas fa-sitemap"></i> Tree View
          </button>
        </div>

        {viewMode === 'grid' && (
          <div className="filters">
            <div className="filter-group">
              <input
                type="text"
                name="search"
                className="form-control"
                placeholder="Search departments..."
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <select
                name="is_active"
                className="form-control"
                value={filters.is_active}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {loading && departments.length === 0 && hierarchy.length === 0 ? (
        <div className="loading">Loading departments...</div>
      ) : viewMode === 'tree' ? (
        <DepartmentTree departments={hierarchy} loading={loading} />
      ) : departments.length === 0 ? (
        <div className="no-departments">
          <i className="fas fa-building"></i>
          <p>No departments found</p>
          <Link to="/app/departments/new" className="btn btn-primary">
            Create Your First Department
          </Link>
        </div>
      ) : (
        <div className="departments-grid">
          {departments.map(department => (
            <DepartmentCard
              key={department.id}
              department={department}
              onDelete={() => handleDeleteClick(department.id)}

            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartmentList;

